import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import {
  GET_INVENTORY_VALUATION,
  GET_FOOD_COST_REPORT,
  GET_COST_DRIVERS,
  GET_WASTE_REPORT,
  GET_VARIANCE_REPORT,
  GET_LOW_STOCK_INGREDIENTS,
  GET_BRANCHES,
} from "./queries";
import { InventoryValuationItem, CATEGORY_LABELS } from "./models";
import InventoryNav from "./inventoryNav";

const InventoryReports: React.FC = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const today = now.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(today);
  const [branchFilter, setBranchFilter] = useState("");

  const branchVar = branchFilter ? parseInt(branchFilter) : undefined;

  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });
  const [fetchValuation, { data: valData, loading: loadingVal }] =
    useLazyQuery(GET_INVENTORY_VALUATION, { fetchPolicy: "no-cache" });
  const [fetchFoodCost, { data: fcData, loading: loadingFC }] = useLazyQuery(
    GET_FOOD_COST_REPORT,
    { fetchPolicy: "no-cache" }
  );
  const [fetchDrivers, { data: driversData, loading: loadingDrivers }] =
    useLazyQuery(GET_COST_DRIVERS, { fetchPolicy: "no-cache" });
  const [fetchWaste, { data: wasteData, loading: loadingWaste }] =
    useLazyQuery(GET_WASTE_REPORT, { fetchPolicy: "no-cache" });
  const [fetchVariance, { data: varData, loading: loadingVar }] =
    useLazyQuery(GET_VARIANCE_REPORT, { fetchPolicy: "no-cache" });
  const [fetchLowStock, { data: lowStockData }] = useLazyQuery(
    GET_LOW_STOCK_INGREDIENTS,
    { fetchPolicy: "no-cache" }
  );

  useEffect(() => {
    fetchBranches();
    fetchLowStock({ variables: { branch_id: branchVar } });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchValuation({ variables: { branch_id: branchVar } });
    fetchLowStock({ variables: { branch_id: branchVar } });
    fetchFoodCost({ variables: { startDate, endDate, branch_id: branchVar } });
    fetchDrivers({
      variables: { startDate, endDate, branch_id: branchVar, limit: 5 },
    });
    fetchWaste({ variables: { startDate, endDate, branch_id: branchVar } });
    fetchVariance({ variables: { startDate, endDate, branch_id: branchVar } });
    //eslint-disable-next-line
  }, [startDate, endDate, branchFilter]);

  const valuation = valData?.getInventoryValuation;
  const foodCost = fcData?.getFoodCostReport;
  const drivers = driversData?.getCostDrivers;
  const waste = wasteData?.getWasteReport;
  const variance = varData?.getVarianceReport;
  const lowStockCount = lowStockData?.getLowStockIngredients?.length || 0;

  const loading =
    loadingVal || loadingFC || loadingDrivers || loadingWaste || loadingVar;

  const fmtMoney = (n: number) =>
    "$" +
    Number(n).toLocaleString("es-MX", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const dateParams = `startDate=${startDate}&endDate=${endDate}${
    branchFilter ? `&branch_id=${branchFilter}` : ""
  }`;

  return (
    <>
      <HeaderTitle title={lang("Reportes de Inventario", "Inventory Reports")} />
      <InventoryNav active="reports" />

      {/* Global Filters */}
      <div className="row mb-3">
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Desde", "From")}
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Hasta", "To")}
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Sucursal", "Branch")}
          </label>
          <select
            className="custom-select custom-select-sm"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <option value="">{lang("Todas", "All")}</option>
            {branchData?.getBranches?.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {/* Row 1: Key Metrics */}
      <div className="row mb-3">
        {/* Inventory Valuation */}
        <div className="col-lg-3">
          <div className="material_card p-3 h-100">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <small className="text-muted font-weight-bold">
                {lang("Valor del Inventario", "Inventory Value")}
              </small>
              <i className="fa fa-warehouse text-primary" />
            </div>
            <h3 className="mb-1">
              {valuation ? fmtMoney(valuation.total_value) : "—"}
            </h3>
            <small className="text-muted">
              {valuation
                ? `${valuation.total_items} ${lang("ingredientes", "ingredients")}`
                : ""}
            </small>
          </div>
        </div>

        {/* Food Cost % */}
        <div className="col-lg-3">
          <Link
            to={`/inventory/food-cost`}
            className="text-decoration-none"
          >
            <div className="material_card p-3 h-100">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <small className="text-muted font-weight-bold">
                  {lang("% Costo Alimento", "Food Cost %")}
                </small>
                <i className="fa fa-utensils text-warning" />
              </div>
              <h3
                className={`mb-1 ${
                  foodCost
                    ? foodCost.food_cost_pct > 35
                      ? "text-danger"
                      : foodCost.food_cost_pct > 25
                      ? "text-warning"
                      : "text-success"
                    : ""
                }`}
              >
                {foodCost ? `${foodCost.food_cost_pct.toFixed(1)}%` : "—"}
              </h3>
              {foodCost && (
                <small className="text-muted">
                  {fmtMoney(foodCost.total_ingredient_cost)} /{" "}
                  {fmtMoney(foodCost.total_revenue)}
                </small>
              )}
            </div>
          </Link>
        </div>

        {/* Waste Cost */}
        <div className="col-lg-3">
          <Link to="/inventory/waste" className="text-decoration-none">
            <div className="material_card p-3 h-100">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <small className="text-muted font-weight-bold">
                  {lang("Costo de Merma", "Waste Cost")}
                </small>
                <i className="fa fa-trash text-danger" />
              </div>
              <h3 className="mb-1 text-danger">
                {waste ? fmtMoney(waste.total_waste_cost) : "—"}
              </h3>
              <small className="text-muted">
                {waste
                  ? `${waste.total_entries} ${lang("registros", "entries")}`
                  : ""}
              </small>
            </div>
          </Link>
        </div>

        {/* Low Stock Alerts */}
        <div className="col-lg-3">
          <Link to="/inventory/alerts" className="text-decoration-none">
            <div className="material_card p-3 h-100">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <small className="text-muted font-weight-bold">
                  {lang("Alertas Stock Bajo", "Low Stock Alerts")}
                </small>
                <i
                  className={`fa fa-exclamation-triangle ${
                    lowStockCount > 0 ? "text-warning" : "text-success"
                  }`}
                />
              </div>
              <h3
                className={`mb-1 ${
                  lowStockCount > 0 ? "text-warning" : "text-success"
                }`}
              >
                {lowStockCount}
              </h3>
              <small className="text-muted">
                {lowStockCount > 0
                  ? lang("ingredientes por debajo del umbral", "ingredients below threshold")
                  : lang("Todo en orden", "All good")}
              </small>
            </div>
          </Link>
        </div>
      </div>

      {/* Row 2: Variance + Top Cost Drivers */}
      <div className="row mb-3">
        {/* Variance Summary */}
        <div className="col-lg-6">
          <div className="material_card p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 font-weight-bold">
                {lang("Resumen de Varianza", "Variance Summary")}
              </h6>
              <Link
                to="/inventory/counts"
                className="btn btn-outline-secondary btn-sm"
              >
                {lang("Ver Conteos", "View Counts")}
              </Link>
            </div>
            {variance ? (
              <div className="row text-center">
                <div className="col-4">
                  <h4>{variance.total_counts}</h4>
                  <small className="text-muted">
                    {lang("Conteos", "Counts")}
                  </small>
                </div>
                <div className="col-4">
                  <h4>{variance.items_with_variance}</h4>
                  <small className="text-muted">
                    {lang("Con varianza", "With variance")}
                  </small>
                </div>
                <div className="col-4">
                  <h4
                    className={
                      variance.total_variance_cost > 0
                        ? "text-danger"
                        : "text-success"
                    }
                  >
                    {fmtMoney(Math.abs(variance.total_variance_cost))}
                  </h4>
                  <small className="text-muted">
                    {lang("Costo varianza", "Variance cost")}
                  </small>
                </div>
              </div>
            ) : (
              <p className="text-muted text-center mb-0">
                {lang("Sin datos", "No data")}
              </p>
            )}
          </div>
        </div>

        {/* Top 5 Cost Drivers */}
        <div className="col-lg-6">
          <div className="material_card p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 font-weight-bold">
                {lang("Top 5 Costos", "Top 5 Cost Drivers")}
              </h6>
              <Link
                to={`/inventory/cost-drivers?${dateParams}`}
                className="btn btn-outline-primary btn-sm"
              >
                {lang("Ver Todos", "View All")}
              </Link>
            </div>
            {drivers && drivers.items.length > 0 ? (
              <table className="table table-sm mb-0">
                <tbody>
                  {drivers.items.map((item: any, idx: number) => (
                    <tr key={item.ingredient?.id || idx}>
                      <td className="border-0 py-1">
                        <span className="font-weight-bold">
                          {item.ingredient?.name || "—"}
                        </span>
                      </td>
                      <td className="border-0 py-1 text-right font-weight-bold">
                        {fmtMoney(item.total_cost)}
                      </td>
                      <td className="border-0 py-1 text-right text-muted">
                        {item.pct_of_total.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-muted text-center mb-0">
                {lang("Sin datos de consumo", "No consumption data")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Inventory Valuation Top Items */}
      <div className="row mb-3">
        <div className="col-lg-12">
          <div className="material_card p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 font-weight-bold">
                {lang(
                  "Ingredientes por Valor de Inventario",
                  "Ingredients by Inventory Value"
                )}
              </h6>
            </div>
            {valuation && valuation.items.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover vertical_middle mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>{lang("Ingrediente", "Ingredient")}</th>
                      <th>{lang("Categoría", "Category")}</th>
                      <th className="text-right">
                        {lang("Stock", "Stock")}
                      </th>
                      <th className="text-right">
                        {lang("Costo Unitario", "Unit Cost")}
                      </th>
                      <th className="text-right">
                        {lang("Valor Total", "Total Value")}
                      </th>
                      <th style={{ width: "20%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {valuation.items
                      .slice(0, 15)
                      .map((item: InventoryValuationItem, idx: number) => {
                        const barWidth =
                          valuation.total_value > 0
                            ? Math.round(
                                (item.total_value / valuation.total_value) * 100
                              )
                            : 0;
                        return (
                          <tr key={item.ingredient?.id || idx}>
                            <td className="font-weight-bold">
                              {item.ingredient?.name || "—"}
                              <small className="text-muted ml-1">
                                ({item.ingredient?.unit})
                              </small>
                            </td>
                            <td>
                              {item.ingredient?.category ? (
                                <span className="badge badge-secondary">
                                  {lang(
                                    CATEGORY_LABELS[item.ingredient.category]?.label || item.ingredient.category,
                                    CATEGORY_LABELS[item.ingredient.category]?.labelEn || item.ingredient.category
                                  )}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="text-right">
                              {Number(item.total_stock).toLocaleString("es-MX", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              {item.ingredient?.unit}
                            </td>
                            <td className="text-right">
                              {fmtMoney(item.cost_per_unit)}
                            </td>
                            <td className="text-right font-weight-bold">
                              {fmtMoney(item.total_value)}
                            </td>
                            <td>
                              <div
                                style={{
                                  height: 14,
                                  background: "#e9ecef",
                                  borderRadius: 4,
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    width: `${barWidth}%`,
                                    background: "#007bff",
                                    borderRadius: 4,
                                    transition: "width 0.3s ease",
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted text-center mb-0">
                {lang("Sin datos de inventario", "No inventory data")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Row 4: Quick Links */}
      <div className="row mb-4">
        <div className="col-lg-12">
          <div className="material_card p-3">
            <h6 className="font-weight-bold mb-3">
              {lang("Reportes Detallados", "Detailed Reports")}
            </h6>
            <div className="row">
              <div className="col-lg-3 col-md-6 mb-2">
                <Link
                  to="/inventory/food-cost"
                  className="btn btn-outline-warning btn-block"
                >
                  <i className="fa fa-utensils mr-2" />
                  {lang("Costo y Rentabilidad", "Cost & Profitability")}
                </Link>
              </div>
              <div className="col-lg-3 col-md-6 mb-2">
                <Link
                  to={`/inventory/cost-drivers?${dateParams}`}
                  className="btn btn-outline-primary btn-block"
                >
                  <i className="fa fa-chart-bar mr-2" />
                  {lang("Top Costos", "Cost Drivers")}
                </Link>
              </div>
              <div className="col-lg-3 col-md-6 mb-2">
                <Link
                  to={`/inventory/theoretical-vs-actual?${dateParams}`}
                  className="btn btn-outline-info btn-block"
                >
                  <i className="fa fa-balance-scale mr-2" />
                  {lang("Teórico vs Real", "Theoretical vs Actual")}
                </Link>
              </div>
              <div className="col-lg-3 col-md-6 mb-2">
                <Link
                  to="/inventory/movements"
                  className="btn btn-outline-secondary btn-block"
                >
                  <i className="fa fa-history mr-2" />
                  {lang("Historial Movimientos", "Movement History")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InventoryReports;
